import { useEffect, useRef, useState } from "react";
import { Box, Divider, Typography } from "@mui/material";

import "./TermsAndConditions.css";

const sections = [
  { id: "acceptance", label: "Acceptance of Terms" },
  { id: "use-of-service", label: "Use of the Service" },
  { id: "user-accounts", label: "User Accounts" },
  { id: "intellectual-property", label: "Intellectual Property" },
  { id: "prohibited-conduct", label: "Prohibited Conduct" },
  { id: "payments", label: "Payments & Subscriptions" },
  { id: "termination", label: "Termination" },
  { id: "disclaimers", label: "Disclaimers" },
  { id: "limitation", label: "Limitation of Liability" },
  { id: "governing-law", label: "Governing Law" },
  { id: "changes", label: "Changes to Terms" },
  { id: "contact", label: "Contact Us" },
];

export function TermsOfService() {
  const [activeSection, setActiveSection] = useState("acceptance");
  const sectionRefs = useRef({});

  useEffect(() => {
    const scroller = document.querySelector(".legalLayoutRoot");

    const handleScroll = () => {
      const OFFSET = 120;
      let currentSection = sections[0].id;
      for (const { id } of sections) {
        const el = sectionRefs.current[id];
        if (el) {
          const rect = el.getBoundingClientRect();
          if (rect.top <= OFFSET) {
            currentSection = id;
          }
        }
      }
      setActiveSection(currentSection);
    };

    const target = scroller || window;
    target.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();
    return () => target.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToSection = (id) => {
    const el = sectionRefs.current[id];
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const setRef = (id) => (el) => {
    sectionRefs.current[id] = el;
  };

  return (
    <Box className="legalPageWrap legalTermsWrap">
      {/* Sticky Sidebar */}
      <Box component="aside" className="legalSidebar">
        <Box component="nav">
          <Box component="ul" className="legalSidebarList">
            {sections.map(({ id, label }) => {
              const isActive = activeSection === id;
              return (
                <Box component="li" key={id} className="legalSidebarItem">
                  <button
                    onClick={() => scrollToSection(id)}
                    className={`legalSidebarBtn ${isActive ? "isActive" : ""}`}
                    type="button"
                  >
                    {label}
                  </button>
                </Box>
              );
            })}
          </Box>
        </Box>
      </Box>

      {/* Main Content */}
      <Box component="main" className="legalMain">
        <Typography className="legalH1" component="h1">
          Terms of Service
        </Typography>
        <Typography className="legalEffective" component="p">
          Effective: May 1, 2026
        </Typography>

        <Box className="legalInfoBanner legalInfoBanner--blue">
          <Box className="legalInfoBannerStripe legalInfoBannerStripe--blue" />
          <Typography
            className="legalInfoBannerText legalInfoBannerText--blue"
            component="p"
          >
            Please read these Terms of Service carefully before using Quest. By
            accessing or using our service, you confirm that you are at least 16
            years old and agree to be legally bound by these terms. If you do
            not agree, please do not use Quest.
          </Typography>
        </Box>

        <Divider className="legalDivider" />

        {/* 1. Acceptance of Terms */}
        <Box
          component="section"
          id="acceptance"
          ref={setRef("acceptance")}
          className="legalSection"
        >
          <SectionTitle number={1} title="Acceptance of Terms" />
          <Typography
            className="legalBody legalMutedText legalBodyHasBottomSmall"
            component="p"
          >
            These Terms of Service ("Terms") constitute a legally binding
            agreement between you ("User", "you") and QGroup ("Quest", "we",
            "us", or "our") governing your use of the Quest web application and
            related services (the "Service").
          </Typography>
          <Typography className="legalBody legalMutedText" component="p">
            By creating an account, clicking "I agree," or otherwise accessing
            the Service, you acknowledge that you have read, understood, and
            agree to be bound by these Terms and our Privacy Policy, which is
            incorporated herein by reference.
          </Typography>
        </Box>

        <Divider className="legalDivider" />

        {/* 2. Use of the Service */}
        <Box
          component="section"
          id="use-of-service"
          ref={setRef("use-of-service")}
          className="legalSection"
        >
          <SectionTitle number={2} title="Use of the Service" />
          <Typography
            className="legalBody legalMutedText legalBodyHasBottomSmall"
            component="p"
          >
            Quest grants you a limited, non-exclusive, non-transferable,
            revocable licence to access and use the Service for your internal
            business or personal purposes, subject to these Terms.
          </Typography>
          <Typography className="legalBody legalMutedText" component="p">
            You agree to use the Service only for lawful purposes and in
            accordance with these Terms. You are responsible for all activities
            that occur under your account and for maintaining the
            confidentiality of your login credentials.
          </Typography>
        </Box>

        <Divider className="legalDivider" />

        {/* 3. User Accounts */}
        <Box
          component="section"
          id="user-accounts"
          ref={setRef("user-accounts")}
          className="legalSection"
        >
          <SectionTitle number={3} title="User Accounts" />
          <Typography className="legalBody legalBodyBlack" component="p">
            To access certain features of Quest, you must register for an
            account. When creating your account, you agree to:
          </Typography>
          <Box
            component="ul"
            className="legalList legalListTight legalMutedText legalListHasBottom"
          >
            <li>
              Provide accurate, current, and complete information during
              registration
            </li>
            <li>
              Maintain and promptly update your account information as necessary
            </li>
            <li>
              Keep your password secure and not share it with any third party
            </li>
            <li>
              Notify us immediately at{" "}
              <a href="mailto:info@quest-q.com" className="legalLink">
                info@quest-q.com
              </a>{" "}
              if you suspect any unauthorised use of your account
            </li>
            <li>
              Accept responsibility for all activities that occur under your
              account
            </li>
          </Box>
          <Typography className="legalBody legalMutedText" component="p">
            We reserve the right to suspend or terminate accounts that contain
            false or misleading information.
          </Typography>
        </Box>

        <Divider className="legalDivider" />

        {/* 4. Intellectual Property */}
        <Box
          component="section"
          id="intellectual-property"
          ref={setRef("intellectual-property")}
          className="legalSection"
        >
          <SectionTitle number={4} title="Intellectual Property" />
          <Typography
            className="legalBody legalMutedText legalBodyHasBottomSmall"
            component="p"
          >
            The Service and all content, features, and functionality — including
            but not limited to text, graphics, logos, icons, software, and
            design — are owned by QGroup or its licensors and are protected by
            applicable intellectual property laws.
          </Typography>
          <Typography className="legalBody legalMutedText" component="p">
            You retain ownership of any content you submit to the Service ("User
            Content"). By submitting User Content, you grant QGroup a
            non-exclusive, worldwide, royalty-free licence to use, store, and
            display that content solely for the purpose of operating and
            improving the Service.{" "}
            <span className="legalEmphasis">
              You may not copy, modify, distribute, sell, or lease any part of
              our Service or its content without our prior written consent.
            </span>
          </Typography>
        </Box>

        <Divider className="legalDivider" />

        {/* 5. Prohibited Conduct */}
        <Box
          component="section"
          id="prohibited-conduct"
          ref={setRef("prohibited-conduct")}
          className="legalSection"
        >
          <SectionTitle number={5} title="Prohibited Conduct" />
          <Typography className="legalBody legalBodyBlack" component="p">
            When using Quest, you agree not to:
          </Typography>
          <Box
            component="ul"
            className="legalList legalListTight legalMutedText"
          >
            <li>
              Violate any applicable local, national, or international law or
              regulation
            </li>
            <li>
              Attempt to gain unauthorised access to any part of the Service or
              its related systems
            </li>
            <li>
              Transmit any harmful, offensive, defamatory, or otherwise
              objectionable content
            </li>
            <li>
              Reverse engineer, decompile, or disassemble any portion of the
              Service
            </li>
            <li>
              Use automated tools, bots, or scrapers to extract data without our
              prior consent
            </li>
            <li>
              Interfere with or disrupt the integrity or performance of the
              Service
            </li>
            <li>Use the Service to send unsolicited communications or spam</li>
            <li>
              Impersonate any person or entity, or misrepresent your affiliation
              with a person or entity
            </li>
          </Box>
        </Box>

        <Divider className="legalDivider" />

        {/* 6. Payments & Subscriptions */}
        <Box
          component="section"
          id="payments"
          ref={setRef("payments")}
          className="legalSection"
        >
          <SectionTitle number={6} title="Payments & Subscriptions" />
          <Typography
            className="legalBody legalMutedText legalBodyHasBottom"
            component="p"
          >
            Certain features of Quest may require payment. By subscribing to a
            paid plan, you agree to pay all applicable fees as described on our
            pricing page. All fees are exclusive of applicable taxes unless
            stated otherwise.
          </Typography>
          <Box
            component="ul"
            className="legalList legalListLoose legalMutedText"
          >
            <li>
              <span className="legalEmphasis">Billing:</span> Subscriptions are
              billed in advance on a recurring basis (monthly or annually) based
              on your chosen plan
            </li>
            <li>
              <span className="legalEmphasis">Cancellations:</span> You may
              cancel your subscription at any time; access continues until the
              end of the current billing period
            </li>
            <li>
              <span className="legalEmphasis">Refunds:</span> Fees are generally
              non-refundable except where required by applicable law
            </li>
            <li>
              <span className="legalEmphasis">Price changes:</span> We will
              provide at least 30 days' notice before implementing pricing
              changes
            </li>
          </Box>
        </Box>

        <Divider className="legalDivider" />

        {/* 7. Termination */}
        <Box
          component="section"
          id="termination"
          ref={setRef("termination")}
          className="legalSection"
        >
          <SectionTitle number={7} title="Termination" />
          <Typography
            className="legalBody legalMutedText legalBodyHasBottomSmall"
            component="p"
          >
            You may terminate your account at any time by contacting us or using
            the account settings within the platform. We reserve the right to
            suspend or terminate your access to the Service, with or without
            notice, if we reasonably believe you have violated these Terms,
            posed a security risk, or engaged in fraudulent or harmful conduct.
          </Typography>
          <Typography className="legalBody legalMutedText" component="p">
            Upon termination, your right to use the Service will immediately
            cease. Provisions of these Terms that by their nature should survive
            termination shall remain in effect.
          </Typography>
        </Box>

        <Divider className="legalDivider" />

        {/* 8. Disclaimers */}
        <Box
          component="section"
          id="disclaimers"
          ref={setRef("disclaimers")}
          className="legalSection"
        >
          <SectionTitle number={8} title="Disclaimers" />
          <Typography
            className="legalBody legalMutedText legalBodyHasBottomSmall"
            component="p"
          >
            The Service is provided on an "as is" and "as available" basis,
            without warranties of any kind, either express or implied. QGroup
            does not warrant that the Service will be uninterrupted, error-free,
            or free of viruses or other harmful components.
          </Typography>
          <Typography className="legalBody legalMutedText" component="p">
            To the fullest extent permitted by applicable law, we disclaim all
            warranties, including implied warranties of merchantability, fitness
            for a particular purpose, and non-infringement.
          </Typography>
        </Box>

        <Divider className="legalDivider" />

        {/* 9. Limitation of Liability */}
        <Box
          component="section"
          id="limitation"
          ref={setRef("limitation")}
          className="legalSection"
        >
          <SectionTitle number={9} title="Limitation of Liability" />
          <Typography
            className="legalBody legalMutedText legalBodyHasBottomSmall"
            component="p"
          >
            To the maximum extent permitted by law, QGroup and its officers,
            directors, employees, and agents shall not be liable for any
            indirect, incidental, special, consequential, or punitive damages
            including loss of profits, data, goodwill, or business opportunities
            arising from your use of or inability to use the Service.
          </Typography>
          <Typography className="legalBody legalMutedText" component="p">
            In no event shall our aggregate liability to you for all claims
            arising out of or related to the Service exceed the total amount you
            paid to QGroup in the twelve months preceding the event giving rise
            to the claim.
          </Typography>
        </Box>

        <Divider className="legalDivider" />

        {/* 10. Governing Law */}
        <Box
          component="section"
          id="governing-law"
          ref={setRef("governing-law")}
          className="legalSection"
        >
          <SectionTitle number={10} title="Governing Law" />
          <Typography
            className="legalBody legalMutedText legalBodyHasBottomSmall"
            component="p"
          >
            These Terms shall be governed by and construed in accordance with
            the laws of the jurisdiction in which QGroup is registered, without
            regard to its conflict of law provisions.
          </Typography>
          <Typography className="legalBody legalMutedText" component="p">
            Any disputes arising out of or relating to these Terms or the
            Service shall be subject to the exclusive jurisdiction of the courts
            in QGroup's registered jurisdiction. You waive any objection to such
            jurisdiction or venue.
          </Typography>
        </Box>

        <Divider className="legalDivider" />

        {/* 11. Changes to Terms */}
        <Box
          component="section"
          id="changes"
          ref={setRef("changes")}
          className="legalSection"
        >
          <SectionTitle number={11} title="Changes to Terms" />
          <Typography
            className="legalBody legalMutedText legalBodyHasBottomSmall"
            component="p"
          >
            We may revise these Terms from time to time. When we make material
            changes, we will notify you by email or through a prominent notice
            within the Service at least 14 days before the changes take effect.
          </Typography>
          <Typography className="legalBody legalMutedText" component="p">
            Your continued use of Quest after the effective date of revised
            Terms constitutes your acceptance of those changes. If you do not
            agree to the new Terms, you must stop using the Service and may
            terminate your account.
          </Typography>
        </Box>

        <Divider className="legalDivider" />

        {/* 12. Contact Us */}
        <Box
          component="section"
          id="contact"
          ref={setRef("contact")}
          className="legalSection legalSectionLast"
        >
          <SectionTitle number={12} title="Contact Us" />
          <Typography
            className="legalBody legalMutedText legalBodyHasBottom"
            component="p"
          >
            If you have any questions about these Terms or wish to report a
            violation, please reach out to us:
          </Typography>
          <Box className="legalContact legalMutedText">
            <Typography component="p" className="legalBody">
              <span className="legalEmphasis">Email:</span>{" "}
              <a href="mailto:info@quest-q.com" className="legalLink">
                info@quest-q.com
              </a>
            </Typography>
            <Typography component="p" className="legalBody legalContactCompany">
              <span className="legalEmphasis">Company:</span> QGroup
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

function SectionTitle({ number, title }) {
  return (
    <Typography component="h2" className="legalSectionTitle">
      {number}. {title}
    </Typography>
  );
}
